import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Video from 'twilio-video';
import axios from 'axios';
import { withStyles } from '@mui/styles';
import { Button, Dialog, DialogActions, DialogContent, FormControl, FormLabel, Grid, MenuItem, TextField } from "@mui/material";
import html2canvas from "html2canvas";
import DeleteIcon from '@mui/icons-material/Delete';
import CircularProgress from '@mui/material/CircularProgress';
import Select from '@mui/material/Select';
import { toast } from 'react-toastify';
import { writeAuditLog } from '../common/apis';
import ConsultSettingsPopup from '../common/ConsultSettingsPopup';
import ConsultDeviceDock from '../common/ConsultDeviceDock';
import { acquireMicrophoneTrack } from '../../utils/audioDeviceUtils';

const styles = (theme) => ({
  // Main video container - flexes to fill remaining middle-column space
  // (parents constrain height so the consult dialog does not scroll).
  videoContainer: {
    position: 'relative',
    width: '100%',
    flex: 1,
    minHeight: '240px',
    backgroundColor: '#1a1a1a',
  },
  videoColumn: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
    overflow: 'hidden',
  },
  videoStage: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
  },
  capturedImagesRow: {
    overflowX: 'auto',
    overflowY: 'hidden',
    flexShrink: 0,
    maxHeight: '110px',
  },
  // Remote video (patient/kiosk) - full size, base layer
  remoteVideoContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    zIndex: 1,
    overflow: 'hidden',
    '& video': {
      width: '100% !important',
      height: '100% !important',
      objectFit: 'cover !important',
      position: 'absolute',
      top: 0,
      left: 0,
    },
    '& audio': {
      display: 'none',
    },
  },
  remoteVideo: {
    width: '100% !important',
    height: '100% !important',
    objectFit: 'cover !important',
    position: 'absolute',
    top: 0,
    left: 0,
  },
  // Local video (pharmacist) - small PIP, top layer
  localVideoContainer: {
    position: 'absolute',
    top: '8px',
    right: '8px',
    width: '120px',
    height: '90px',
    zIndex: 10,
    borderRadius: '8px',
    overflow: 'hidden',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.5)',
    border: '2px solid rgba(255, 255, 255, 0.3)',
    backgroundColor: '#000',
  },
  localVideo: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  },
  // Pre-connect: local video full size (before patient joins)
  localVideoFullSize: {
    position: 'relative',
    width: '100%',
    height: '100%',
  },
  // Disconnect overlay - shown when patient disconnects
  disconnectOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 15,
    color: '#fff',
    fontSize: '18px',
    fontWeight: 500,
  },
  disconnectIcon: {
    fontSize: '48px',
    marginBottom: '12px',
    opacity: 0.9,
  },
  // Reconnecting overlay - shown during network instability
  reconnectingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    padding: '10px 16px',
    backgroundColor: 'rgba(255, 152, 0, 0.95)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 25,
    color: '#fff',
    fontSize: '13px',
    fontWeight: 500,
    gap: '10px',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
  },
  // Banner when camera was unavailable and we connected with audio only
  audioOnlyFallbackBanner: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    padding: '8px 16px',
    backgroundColor: 'rgba(33, 150, 243, 0.95)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 24,
    color: '#fff',
    fontSize: '12px',
    fontWeight: 500,
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
  },
  // Legacy classes kept for backwards compatibility during transition
  localVideoOnConnect: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  },
  remoteVideoOnConnect: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  primaryButtonVideo: {
    background: '#6cca98',
    color: '#fff !important',
    '&:hover': {
      background: '#6cca98',
      color: '#fff !important',
    },
    '&:disabled': {
      color: 'rgba(0, 0, 0, 0.38)',
      cursor: 'not-allowed',
      background: '#E4E5E8',
    },
  },
  forceAcceptRejectButtons: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '8px',
    marginTop: '10px',
    padding: '0 10px',
    flexShrink: 0,
  },
  deviceBarOverlay: {
    position: 'absolute',
    left: '50%',
    transform: 'translateX(-50%)',
    bottom: 12,
    zIndex: 26,
    backgroundColor: 'transparent',
    border: 'none',
    padding: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    maxWidth: 'calc(100% - 24px)',
  },
});

class TwilioVideo extends Component {
  constructor(props) {
    super(props);
    this.state = {
      identity: null /* Will hold the name assigned to the client. */,
      roomName: '' /* Will store the room name */,
      roomNameErr: false /* Track error for room name TextField. This will    enable us to show an error message when this variable is true */,
      previewTracks: null,
      localMediaAvailable: false /* Represents the availability of a LocalAudioTrack(microphone) and a LocalVideoTrack(camera) */,
      hasJoinedRoom: false,
      activeRoom: null, // Track the current active room
      capturedImages: [],
      currentImage: 0,
      viewerIsOpen: false,
      viewRejectReasons: false,
      rejectItems: [],
      showInnerSpinner: false,
      consultStatus: false,
      captureIsInProcess: false,
      showSettingsPopup: false,
      settingsAnchorEl: null,
      isRoomDisconnected: false, // Track when patient disconnects from the room
      connectionStatus: 'connected', // Track connection status: 'connected', 'reconnecting', 'disconnected'
      audioOnlyFallback: false // True when camera was unavailable and we connected with audio only
    };
    this.joinRoom = this.joinRoom.bind(this);
    this.leaveRoom = this.leaveRoom.bind(this);
    this._isLocalDisconnect = false; // Track if disconnect was initiated locally
  }

  componentDidMount() {
    this._mountTime = Date.now();
    this.props.onRef(this);
    console.log('Connected to Twilio: ' + this.props.identity);
    
    // ✅ CRITICAL FIX: Check if we're resuming a held call with an existing room
    // When resuming a held call, the TwilioVideo component remounts but the room is already connected
    // We need to reuse the existing room and re-attach tracks instead of joining again
    const existingTwilioRef = this.props.existingTwilioRef;
    
    // ✅ ROOT CAUSE FIX: Verify that existingTwilioRef matches the current roomName
    // This prevents using the wrong Twilio instance when switching between multiple calls
    const existingRoomState =
      existingTwilioRef &&
      existingTwilioRef.state &&
      existingTwilioRef.state.activeRoom &&
      existingTwilioRef.state.activeRoom.state;
    const isExistingRoomConnected = existingRoomState === 'connected';
    const isResumingHeldCall = existingTwilioRef && 
        existingTwilioRef.state && 
        existingTwilioRef.state.activeRoom &&
        existingTwilioRef.state.activeRoom.name &&
        existingTwilioRef.state.activeRoom.name.toLowerCase() === this.props.roomName.toLowerCase() &&
        isExistingRoomConnected &&
        !existingTwilioRef.state.isRoomDisconnected;

    console.log('📊 [Twilio] Mount path decision', {
      roomName: this.props.roomName,
      existingRoomName:
        existingTwilioRef &&
        existingTwilioRef.state &&
        existingTwilioRef.state.activeRoom &&
        existingTwilioRef.state.activeRoom.name,
      existingRoomState: existingRoomState || null,
      existingIsRoomDisconnected: Boolean(
        existingTwilioRef &&
          existingTwilioRef.state &&
          existingTwilioRef.state.isRoomDisconnected
      ),
      resumePath: Boolean(isResumingHeldCall)
    });
    
    if (isResumingHeldCall) {
      console.log('🔄 [Twilio] Resuming held call - reusing existing room connection', {
        roomName: this.props.roomName,
        existingRoomName: existingTwilioRef.state.activeRoom.name,
        roomSid: existingTwilioRef.state.activeRoom.sid,
        existingTwilioRefState: existingTwilioRef.state
      });
      
      // Reuse the existing room
      const existingRoom = existingTwilioRef.state.activeRoom;
      this.setState({
        activeRoom: existingRoom,
        localMediaAvailable: true,
        hasJoinedRoom: true,
        identity: this.props.identity + ' Remote',
        token: existingTwilioRef.state.token // Reuse token if available
      });
      
      // ✅ ROOT CAUSE FIX: Clean up ALL video/audio elements from previous call before re-attaching
      // This prevents duplicate video frames when switching between multiple calls
      setTimeout(async () => {
        this.cleanupPreviousCallMedia();
        
        // ✅ FIX ISSUE 1: Enable tracks FIRST before reattaching to ensure they're active
        // When a call is put on hold, tracks are disabled, so we need to enable them before reattaching
        existingRoom.localParticipant.audioTracks.forEach(publication => {
          if (publication.track) {
            publication.track.enable();
            console.log('🔄 [Twilio] Enabled audio track before reattachment');
          }
        });
        existingRoom.localParticipant.videoTracks.forEach(publication => {
          if (publication.track) {
            publication.track.enable();
            console.log('🔄 [Twilio] Enabled video track before reattachment');
          }
        });

        // ✅ CRITICAL: Ensure local AUDIO track is still alive after hold
        // When the call is put on hold, componentWillUnmount() stops local tracks.
        // For video we already recreate the track if needed, but for audio we were only
        // calling track.enable(), which does nothing if the underlying MediaStreamTrack
        // has been stopped. That is why, after resuming from hold, the pharmacist's
        // microphone was silent on the kiosk side until they manually changed the
        // microphone in settings (which recreates and republishes an audio track).
        //
        // To mirror that behavior automatically on resume, we recreate and publish
        // a fresh LocalAudioTrack when the existing one is no longer live.
        try {
          const audioPublications = [...existingRoom.localParticipant.audioTracks.values()];
          if (audioPublications.length > 0 && audioPublications[0].track) {
            const existingAudioTrack = audioPublications[0].track;
            const mediaTrack = existingAudioTrack.mediaStreamTrack;
            const isLive =
              mediaTrack &&
              typeof mediaTrack.readyState === 'string' &&
              mediaTrack.readyState === 'live';

            if (!isLive) {
              console.warn('⚠️ [Twilio] Local audio track is not live when resuming held call, recreating track...', {
                readyState: mediaTrack && mediaTrack.readyState
              });

              try {
                const micDeviceId = localStorage.getItem('selectedConsultMicInput');
                const audioOptions = {};
                if (micDeviceId) {
                  audioOptions.deviceId = { ideal: micDeviceId };
                }

                // Create a new local audio track using preferred mic if available
                const newAudioTrack = await Video.createLocalAudioTrack(audioOptions);

                // Unpublish and stop the old track
                existingRoom.localParticipant.unpublishTrack(existingAudioTrack);
                existingAudioTrack.stop();
                if (mediaTrack && typeof mediaTrack.stop === 'function') {
                  try {
                    mediaTrack.stop();
                  } catch (e) {
                    console.warn('⚠️ [Twilio] Error stopping old audio MediaStreamTrack on resume:', e);
                  }
                }

                // Publish the new audio track so the kiosk hears the pharmacist again
                await existingRoom.localParticipant.publishTrack(newAudioTrack);
                console.log('✅ [Twilio] Recreated and published local audio track after resuming held call');
              } catch (audioError) {
                console.error('❌ [Twilio] Failed to recreate local audio track when resuming held call', audioError);
              }
            }
          } else {
            console.warn('⚠️ [Twilio] No local audio tracks found when resuming held call');
          }
        } catch (audioOuterError) {
          console.error('❌ [Twilio] Unexpected error while verifying/recreating local audio track on resume', audioOuterError);
        }
        
        // Re-attach local video track (now that it's enabled)
        const localVideoTracks = [...existingRoom.localParticipant.videoTracks.values()];
        if (localVideoTracks.length > 0 && localVideoTracks[0].track) {
          const localTrack = localVideoTracks[0].track;
          // ✅ FIX ISSUE 1: Ensure track is enabled and has media before reattaching
          // If track is not ready after enabling, recreate it
          const attemptReattach = async (track, retryCount = 0) => {
            const isReady = track.isEnabled !== false && 
                           track.mediaStreamTrack && 
                           track.mediaStreamTrack.readyState === 'live';
            
            if (isReady) {
              this.reattachLocalVideoTrack(track, existingRoom);
            } else if (retryCount < 3) {
              console.warn(`⚠️ [Twilio] Local video track not ready for reattachment, retrying (${retryCount + 1}/3)...`, {
                isEnabled: track.isEnabled,
                readyState: track.mediaStreamTrack && track.mediaStreamTrack.readyState
              });
              // Retry after increasing delay
              setTimeout(() => attemptReattach(track, retryCount + 1), 200 * (retryCount + 1));
            } else {
              // ✅ FIX ISSUE 1: If track is still not ready after retries, recreate it
              console.warn('⚠️ [Twilio] Local video track not ready after retries, recreating track...', {
                isEnabled: track.isEnabled,
                readyState: track.mediaStreamTrack && track.mediaStreamTrack.readyState
              });
              
              try {
                // Unpublish and stop old track
                existingRoom.localParticipant.unpublishTrack(track);
                track.stop();
                
                // Create new local video track
                // ✅ FIX: Use 'exact' constraint to force selected camera (same as switchCamera)
                const cameraDeviceId = localStorage.getItem("selectedConsultCamera");
                const constraints = cameraDeviceId 
                  ? { deviceId: { exact: cameraDeviceId }, width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 24 } }
                  : { width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 24 } };
                
                let newLocalTrack;
                try {
                  newLocalTrack = await Video.createLocalVideoTrack(constraints);
                } catch (exactErr) {
                  // ✅ FIX: Fallback to 'ideal' if 'exact' fails
                  console.warn('⚠️ [Twilio] Failed to create track with exact device, falling back to ideal:', exactErr.message);
                  const fallbackConstraints = cameraDeviceId 
                    ? { deviceId: { ideal: cameraDeviceId }, width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 24 } }
                    : { width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 24 } };
                  newLocalTrack = await Video.createLocalVideoTrack(fallbackConstraints);
                }
                
                // Publish new track
                await existingRoom.localParticipant.publishTrack(newLocalTrack);
                
                // Attach new track
                this.reattachLocalVideoTrack(newLocalTrack, existingRoom);
                
                console.log('✅ [Twilio] Recreated and attached local video track');
              } catch (error) {
                console.error('❌ [Twilio] Failed to recreate local video track', error);
              }
            }
          };
          
          attemptReattach(localTrack);
        } else {
          console.warn('⚠️ [Twilio] No local video tracks found when resuming held call');
        }
        
        // Re-attach remote participant tracks
        const remoteParticipants = Array.from(existingRoom.participants.values());
        remoteParticipants.forEach(participant => {
          this.reattachParticipantTracks(participant, existingRoom);
        });
        
        // Re-add event listeners (they were removed in componentWillUnmount when call was put on hold)
        // Remove existing listeners first to prevent duplicates
        existingRoom.removeAllListeners('participantConnected');
        existingRoom.removeAllListeners('participantDisconnected');
        
        // Listen for new participants
        existingRoom.on('participantConnected', (participant) => {
          console.log('🔄 [Twilio] New participant connected to resumed call', {
            participantIdentity: participant.identity
          });
          this.reattachParticipantTracks(participant, existingRoom);
        });
        
        // Listen for participant disconnections
        existingRoom.on('participantDisconnected', (participant) => {
          console.log('🔄 [Twilio] Participant disconnected from resumed call', {
            participantIdentity: participant.identity
          });
          this.removeParticipantFromResumedCall(participant, existingRoom);
        });
        
        this.fetchExistingIds();
      }, 100);
      
      return Promise.resolve();
    }
    
    // Validate required props before making API call
    const identity = this.props.identity || this.props.roomName || 'Unknown';
    const roomName = this.props.roomName;
    
    if (!roomName) {
      console.warn('⚠️ [Twilio] Waiting for roomName prop - skipping token fetch');
      return Promise.resolve(); // Don't throw - component may receive valid props later
    }
    
    console.log('📞 [Twilio] Fetching token for room:', { identity, roomName });
    
    // ✅ OPTIMIZATION: Run token fetch and camera warmup in PARALLEL
    // This saves ~500ms by not waiting for token before starting camera
    const tokenPromise = axios.get(
      `${process.env.REACT_APP_BASEURL}twilioToken/${identity} Remote/${roomName}`,
        {
          headers: {
            Authorization:
              'Bearer ' +
              JSON.parse(sessionStorage.getItem('user')).signInUserSession
                .idToken.jwtToken,
            'Content-Type': 'application/json',
          },
        }
    );
    
    // Start camera warmup with user's preferred devices
    const cameraPromise = this.warmupCamera();
    
    // Wait for both to complete
    return Promise.all([tokenPromise, cameraPromise])
      .then(([response, warmupTracks]) => {
        const { identity, token } = response.data;
        // Store pre-warmed tracks for joinRoom to use
        this._preWarmedTracks = warmupTracks;
        this.setState({ identity, token }, () => {
          console.log('✅ [Twilio] Token and camera ready in parallel');
        this.joinRoom();
        setTimeout(() => {
          this.fetchExistingIds();
        }, 2000);
        });
      })
      .catch((err) => {
        console.error('❌ [Twilio] Failed to initialize:', err);
        // Clean up any warmed tracks on failure
        if (this._preWarmedTracks) {
          this._preWarmedTracks.forEach(track => track.stop());
          this._preWarmedTracks = null;
        }
      });
  }
  
  // ✅ OPTIMIZATION: Pre-warm camera with user's preferred devices
  async warmupCamera() {
    const cameraDeviceId = localStorage.getItem("selectedConsultCamera");
    const micDeviceId = localStorage.getItem("selectedConsultMicInput");
    
    // ✅ FIX: Use 'exact' constraint to force selected camera (same as switchCamera)
    // This ensures the selected camera from Consult Settings is used by default
    const constraints = {
      video: cameraDeviceId ? { deviceId: { exact: cameraDeviceId } } : true,
      audio: micDeviceId ? { deviceId: { exact: micDeviceId } } : true
    };
    
    console.log('🎥 [Twilio] Warming up camera with selected devices (exact constraint):', { cameraDeviceId, micDeviceId });
    
    try {
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      const videoTrack = stream.getVideoTracks()[0];
      const audioTrack = stream.getAudioTracks()[0];
      console.log('✅ [Twilio] Camera warmed up successfully with selected device');
      return [videoTrack, audioTrack];
    } catch (err) {
      console.warn('⚠️ [Twilio] Camera warmup failed with exact constraint, falling back to ideal:', err.message);
      // ✅ FIX: Fallback to 'ideal' if 'exact' fails (device might be temporarily unavailable)
      const fallbackConstraints = {
        video: cameraDeviceId ? { deviceId: { ideal: cameraDeviceId } } : true,
        audio: micDeviceId ? { deviceId: { ideal: micDeviceId } } : true
      };
      try {
        const fallbackStream = await navigator.mediaDevices.getUserMedia(fallbackConstraints);
        const videoTrack = fallbackStream.getVideoTracks()[0];
        const audioTrack = fallbackStream.getAudioTracks()[0];
        console.log('✅ [Twilio] Camera warmed up with fallback (ideal constraint)');
        return [videoTrack, audioTrack];
      } catch (fallbackErr) {
        console.warn('⚠️ [Twilio] Camera warmup failed with fallback, trying audio-only:', fallbackErr.message);
        const audioTrack = await acquireMicrophoneTrack(micDeviceId);
        if (audioTrack) {
          console.log('✅ [Twilio] Audio-only fallback: microphone ready (no camera)');
          if (this.props.onAudioOnlyFallbackChange) {
            this.props.onAudioOnlyFallbackChange(true);
          }
          return [null, audioTrack];
        }
        console.warn('⚠️ [Twilio] Audio-only fallback failed; joinRoom will retry');
        return null;
      }
    }
  }
  
    // ✅ ROOT CAUSE FIX: Clean up all media elements from previous call before resuming
    // This prevents duplicate video frames when switching between multiple calls
    cleanupPreviousCallMedia() {
      console.log('🧹 [Twilio] Cleaning up previous call media elements', {
        localMediaExists: !!this.localMedia,
        remoteMediaExists: !!this.remoteMedia,
        roomName: this.props.roomName
      });
      
      // Clean up local video container (pharmacist PIP)
      if (this.localMedia) {
        const localVideoElements = Array.from(this.localMedia.querySelectorAll('video, audio'));
        console.log('🧹 [Twilio] Found local media elements to clean up', { count: localVideoElements.length });
        localVideoElements.forEach(element => {
          try {
            if (element.parentNode) {
              element.parentNode.removeChild(element);
            }
          } catch (e) {
            console.warn('⚠️ [Twilio] Error removing local element during cleanup', e);
          }
        });
      }
      
      // Clean up remote video container (patient)
      if (this.remoteMedia) {
        const remoteVideoElements = Array.from(this.remoteMedia.querySelectorAll('video, audio'));
        console.log('🧹 [Twilio] Found remote media elements to clean up', { count: remoteVideoElements.length });
        remoteVideoElements.forEach(element => {
          try {
            if (element.parentNode) {
              element.parentNode.removeChild(element);
            }
          } catch (e) {
            console.warn('⚠️ [Twilio] Error removing remote element during cleanup', e);
          }
        });
      }
      
      console.log('✅ [Twilio] Cleanup complete - DOM is ready for new tracks', {
        localContainerExists: !!this.localMedia,
        remoteContainerExists: !!this.remoteMedia
      });
    }
   
   // ✅ ROOT CAUSE FIX: Helper function to re-attach local video track when resuming
   reattachLocalVideoTrack(cameraTrack, room) {
     if (!this.localMedia) {
       console.warn('⚠️ [Twilio] localMedia ref not available, retrying in 50ms');
       setTimeout(() => this.reattachLocalVideoTrack(cameraTrack, room), 50);
       return;
     }
     
     try {
       // ✅ ROOT CAUSE FIX: Detach from any previous elements to prevent duplicate attachments
       if (cameraTrack.attachedElements && cameraTrack.attachedElements.length > 0) {
         console.log('🧹 [Twilio] Detaching local video track from previous elements', {
           attachedElementsCount: cameraTrack.attachedElements.length
         });
         cameraTrack.attachedElements.forEach(element => {
           cameraTrack.detach(element);
         });
       }
       
       // ✅ ROOT CAUSE FIX: Remove any existing local video elements from DOM first
       // This prevents duplicate local video frames when switching between multiple calls
       const existingLocalVideos = this.localMedia.querySelectorAll('video');
       existingLocalVideos.forEach(videoElement => {
         // Only remove videos that are direct children of #local (not inside #remote)
         if (videoElement.parentElement && videoElement.parentElement.id === 'local') {
           if (videoElement.parentNode) {
             videoElement.parentNode.removeChild(videoElement);
           }
         }
       });
       
       // Attach local video track with proper class
       const localVideoElement = cameraTrack.attach();
       localVideoElement.className = this.props.classes.localVideo;
       this.localMedia.appendChild(localVideoElement);
       console.log('✅ [Twilio] Attached local video track to DOM with localVideo class');
     } catch (error) {
       console.error('❌ [Twilio] Error re-attaching local video track', {
         error: error.message,
         trackId: cameraTrack.id
       });
     }
   }
   
   // ✅ CRITICAL FIX: Helper function to re-attach participant tracks when resuming a held call
   async reattachParticipantTracks(participant, room) {
     const { classes } = this.props;
     const previewContainer1 = this.remoteMedia;
     
     console.log('🔄 [Twilio] Re-attaching tracks for participant', {
            participantIdentity: participant.identity,
            roomName: room.name,
       remoteMediaRefExists: !!previewContainer1
     });
     
     // Ensure remoteMedia ref is available
     if (!previewContainer1) {
       console.warn('⚠️ [Twilio] remoteMedia ref not available yet, retrying in 100ms');
       setTimeout(() => this.reattachParticipantTracks(participant, room), 100);
       return;
     }
     
     // ✅ ROOT CAUSE FIX: Properly detach all existing tracks before clearing
     // This prevents duplicate video frames and "Track is already attached" errors
     // When switching between multiple calls, we need to ensure all previous tracks are detached
     const existingElements = Array.from(previewContainer1.children);
     console.log('🧹 [Twilio] Cleaning up existing elements in remote container', {
       elementCount: existingElements.length,
              roomName: room.name,
       participantIdentity: participant.identity
     });
     
     existingElements.forEach(element => {
       // Try to detach from Twilio tracks if they're attached
       // Twilio tracks are attached directly, so we need to check if element has attached tracks
       try {
         // Remove element from DOM - Twilio will handle track cleanup
         if (element.parentNode) {
           element.parentNode.removeChild(element);
         }
       } catch (e) {
         console.warn('⚠️ [Twilio] Error removing element during cleanup', e);
       }
     });
     
     // ✅ CRITICAL: Clear the container completely to remove any orphaned elements
     previewContainer1.innerHTML = '';
     console.log('✅ [Twilio] Cleared remote container, ready for new tracks');
     
     // ✅ CRITICAL FIX: Ensure the remote container structure is maintained
     // The remote div should exist and be ready to receive video elements
     const remoteVideo = document.getElementById('remote');
     
     if (!remoteVideo) {
       console.error('❌ [Twilio] Remote video container (#remote) not found in DOM');
       return;
     }
     
     // ✅ ROOT CAUSE FIX: Re-attach all existing tracks with proper async handling
     // This ensures audio tracks are properly routed and played when resuming a held call
     for (const publication of participant.tracks.values()) {
       if (publication && publication.track && publication.isSubscribed) {
         const track = publication.track;
         
         try {
           // ✅ CRITICAL FIX: Detach track from any previous element before re-attaching
           // This prevents "Track is already attached" errors
           if (track.attachedElements && track.attachedElements.length > 0) {
             track.attachedElements.forEach(element => {
               track.detach(element);
             });
           }
           
           const element = track.attach();

           // ✅ ROOT CAUSE FIX: For audio tracks, await setSinkId and explicitly play
           // This ensures the audio device is set before playback and that playback resumes
           if (track.kind === 'audio') {
             // Set autoplay attribute to ensure automatic playback
             if (element.setAttribute) {
               element.setAttribute('autoplay', 'true');
             }
             
             // Route audio to the selected output device
             if (typeof element.setSinkId === 'function') {
               const callOutputDeviceId = localStorage.getItem("selectedConsultCallingDevice");
               if (callOutputDeviceId) {
                 try {
                   await element.setSinkId(callOutputDeviceId);
                   console.log(`✅ Re-attached Twilio call audio routed to device: ${callOutputDeviceId}`);
                 } catch (err) {
                   console.warn("⚠️ Failed to setSinkId on re-attached audio element:", err);
                 }
               }
             }
             
             // ✅ CRITICAL FIX: Explicitly play the audio element to ensure playback resumes
             // After reattachment, the audio element may not automatically play, especially
             // if it was previously detached. This ensures audio is heard immediately.
             try {
               const playPromise = element.play();
               if (playPromise !== undefined) {
                 await playPromise;
                 console.log('✅ [Twilio] Audio element playback resumed successfully');
               }
             } catch (playError) {
               // Some browsers may block autoplay - log but don't fail
               console.warn('⚠️ [Twilio] Could not auto-play audio element (may require user interaction):', playError.message);
             }
           }

          // Apply correct class based on track type
          if (track.kind === 'video') {
            element.className = classes.remoteVideo;
          }
          
          // Append to remoteMedia container
          if (previewContainer1) {
            previewContainer1.appendChild(element);
            console.log('✅ [Twilio] Appended track element to remote container', {
              trackKind: track.kind,
              trackId: track.id
            });
          }
          
          console.log('✅ Re-attached track', { kind: track.kind, trackId: track.id });
         } catch (error) {
           console.error('❌ [Twilio] Error re-attaching track', {
             error: error.message,
             trackKind: track.kind,
             trackId: track.id
           });
         }
       }
     }
     
     // ✅ CRITICAL FIX: Remove existing listeners before adding new ones to prevent duplicates
     participant.removeAllListeners('trackSubscribed');
     
     // Listen for new tracks
     participant.on('trackSubscribed', async (track) => {
       console.log('🔄 [Twilio] New track subscribed during resumed call', {
         trackKind: track.kind,
         trackId: track.id
       });
       
       try {
         // Detach from any previous elements
         if (track.attachedElements && track.attachedElements.length > 0) {
           track.attachedElements.forEach(element => {
             track.detach(element);
           });
         }
         
        const element = track.attach();

        // ✅ ROOT CAUSE FIX: For audio tracks, await setSinkId and explicitly play
        // This ensures the audio device is set before playback and that playback resumes
        if (track.kind === 'audio') {
          // Set autoplay attribute to ensure automatic playback
          if (element.setAttribute) {
            element.setAttribute('autoplay', 'true');
          }
          
          // Route audio to the selected output device
          if (typeof element.setSinkId === 'function') {
            const callOutputDeviceId = localStorage.getItem("selectedConsultCallingDevice");
            if (callOutputDeviceId) {
              try {
                await element.setSinkId(callOutputDeviceId);
                console.log(`✅ New track audio routed to device: ${callOutputDeviceId}`);
              } catch (err) {
                console.warn("⚠️ Failed to setSinkId on new audio element:", err);
              }
            }
          }
          
          // ✅ CRITICAL FIX: Explicitly play the audio element to ensure playback resumes
          // After reattachment, the audio element may not automatically play, especially
          // if it was previously detached. This ensures audio is heard immediately.
          try {
            const playPromise = element.play();
            if (playPromise !== undefined) {
              await playPromise;
              console.log('✅ [Twilio] New audio track playback started successfully');
            }
          } catch (playError) {
            // Some browsers may block autoplay - log but don't fail
            console.warn('⚠️ [Twilio] Could not auto-play new audio element (may require user interaction):', playError.message);
          }
        }

        // Apply correct class based on track type
        if (track.kind === 'video') {
          element.className = classes.remoteVideo;
        }

        if (previewContainer1) {
          previewContainer1.appendChild(element);
        }
        
        console.log('✅ Re-attached new track', { kind: track.kind, trackId: track.id });
       } catch (error) {
         console.error('❌ [Twilio] Error attaching new track', {
           error: error.message,
           trackKind: track.kind,
           trackId: track.id
         });
       }
     });
   }
  
  // ✅ CRITICAL FIX: Helper function to remove participant when resuming a held call
  removeParticipantFromResumedCall(participant, room) {
    const { classes } = this.props;
    
    console.log('🔄 [Twilio] Removing participant from resumed call', {
            participantIdentity: participant.identity,
            roomName: room.name
          });
          
          const elem = document.getElementById('remote');
          if (elem && elem.parentNode) {
            try {
              elem.parentNode.removeChild(elem);
        console.log('✅ Successfully removed remote video element');
            } catch (error) {
        console.warn('⚠️ Error removing remote video element:', error);
            }
          }
          
          let localVideo = document.getElementById('local');
          if (localVideo && localVideo.children && localVideo.children[1]) {
            localVideo.children[1].className = classes.localVideo;
          }
        }

  componentWillUnmount() {
    // ✅ CRITICAL: When component unmounts, we need to be careful about disconnecting
    // If the call is being put on hold, we should NOT disconnect (room is stored in heldCalls)
    // If the call is actually ending, leaveRoom() will be called explicitly before unmount
    // So we only clean up event listeners here, NOT disconnect
    // This prevents held calls from being disconnected when switching to a new call
    if (this.state.activeRoom) {
      console.log('🧹 [Twilio] Component unmounting, cleaning up', {
        roomName: this.state.activeRoom.name,
        roomSid: this.state.activeRoom.sid
      });
      
      // Remove all event listeners to prevent processing events from held/inactive rooms
      this.state.activeRoom.removeAllListeners();
      
      // ✅ FIX: Always stop local tracks on unmount to release camera/mic
      // This is safe even for held calls - we can recreate tracks when resuming
      const tracks = this.state.activeRoom.localParticipant.tracks;
      tracks.forEach((publication) => {
        if (publication.track) {
          publication.track.stop();
        }
      });
    }
    
    this.props.onRef(undefined);
  }

  leaveRoom(isPharmacistInitiated = true) {
    // isPharmacistInitiated: true = pharmacist clicked Complete Consult, false = socket event (patient ended call)
    // ✅ CRITICAL FIX: Verify room name matches before disconnecting to prevent affecting other pharmacists' calls
    // This ensures we're only disconnecting from the correct room
    const currentRoomName = this.props.roomName;
    const activeRoomName = this.state.activeRoom ? this.state.activeRoom.name : null;
    
    console.log('🔍 [Twilio] leaveRoom: Verifying room before disconnect', {
      currentRoomNameProp: currentRoomName,
      activeRoomName: activeRoomName,
      roomNameMatches: currentRoomName && activeRoomName && currentRoomName.toLowerCase() === activeRoomName.toLowerCase(),
      hasActiveRoom: !!this.state.activeRoom
    });
    
    // Only disconnect if room name matches - this prevents disconnecting from wrong room
    if (this.state.activeRoom && currentRoomName && activeRoomName && 
        currentRoomName.toLowerCase() === activeRoomName.toLowerCase()) {
      console.log('✅ [Twilio] leaveRoom: Room names match, disconnecting from room', {
        roomName: activeRoomName,
        roomSid: this.state.activeRoom.sid
      });
      // ✅ FIX: Stop tracks BEFORE disconnecting to properly release camera/mic
      this.state.activeRoom.localParticipant.tracks.forEach((publication) => {
        // publication.track is the actual LocalTrack, not publication itself
        if (publication.track) {
          // Stop the Twilio track
          publication.track.stop();
          // ✅ H19 FIX: Also stop the underlying MediaStreamTrack to fully release camera/mic
          if (publication.track.mediaStreamTrack) {
            publication.track.mediaStreamTrack.stop();
          }
          console.log('🛑 [Twilio] Stopped track:', publication.track.kind);
        }
      });
      // Mark this as a local disconnect only if pharmacist initiated (not socket event)
      this._isLocalDisconnect = isPharmacistInitiated;
      this.state.activeRoom.disconnect();
    } else {
      console.warn('⚠️ [Twilio] leaveRoom: Room name mismatch or missing data - NOT disconnecting', {
        currentRoomNameProp: currentRoomName,
        activeRoomName: activeRoomName,
        hasActiveRoom: !!this.state.activeRoom,
        reason: !currentRoomName ? 'No roomName prop' : !activeRoomName ? 'No active room name' : 'Room names do not match',
        note: 'This prevents affecting other pharmacists\' active calls'
      });
      // Still update state to reflect that we're not in a room anymore
      this.setState({ hasJoinedRoom: false, localMediaAvailable: false, activeRoom: null, audioOnlyFallback: false });
      return Promise.resolve({ data: 'Room name mismatch - disconnect skipped' });
    }

    return axios
      .get(
        `${process.env.REACT_APP_BASEURL}twilioToken/${this.props.roomName}/completed`,
        {
          headers: {
            Authorization:
              'Bearer ' +
              JSON.parse(sessionStorage.getItem('user')).signInUserSession
                .idToken.jwtToken,
            'Content-Type': 'application/json',
          },
        }
      )
      .then(({ data }) => {
        console.log('Room disconnected!!!!!', data);
        this.setState({ hasJoinedRoom: false, localMediaAvailable: false, activeRoom: null, audioOnlyFallback: false });
      })
      .catch((error) => {
        console.error('❌ [Twilio] leaveRoom: Error calling completed endpoint', error);
        // Still update state even if API call fails
        this.setState({ hasJoinedRoom: false, localMediaAvailable: false, activeRoom: null, audioOnlyFallback: false });
      });
  }

  // Handle device switch from settings popup
  handleDeviceSwitch = async (deviceType, deviceId) => {
    console.log(`🔄 [Twilio] Device switch requested: ${deviceType} -> ${deviceId}`);
    try {
      switch (deviceType) {
        case 'microphone':
          await this.switchMicrophone(deviceId);
          break;
        case 'camera':
          await this.switchCamera(deviceId);
          break;
        case 'speaker':
          await this.switchSpeaker(deviceId);
          break;
        default:
          console.warn(`Unknown device type: ${deviceType}`);
      }
    } catch (error) {
      console.error(`❌ [Twilio] Failed to switch ${deviceType}:`, error);
    }
  };

  // Switch microphone during active call
  switchMicrophone = async (newDeviceId) => {
    const room = this.state.activeRoom;
    if (!room) {
      console.warn('⚠️ [Twilio] Cannot switch microphone - no active room');
      return;
    }

    // Prevent concurrent microphone switches
    if (this.isSwitchingMicrophone) {
      console.warn('⚠️ [Twilio] Microphone switch already in progress, ignoring');
      return;
    }
    this.isSwitchingMicrophone = true;

    const localParticipant = room.localParticipant;
    console.log('🎤 [Twilio] Switching microphone to:', newDeviceId);

    try {
      // Get current audio track
      const audioTracks = [...localParticipant.audioTracks.values()];
      const currentAudioPublication = audioTracks[0];
      const currentAudioTrack = currentAudioPublication ? currentAudioPublication.track : null;

      // Create new audio track with new device
      const newAudioTrack = await Video.createLocalAudioTrack({
        deviceId: { exact: newDeviceId }
      });

      // Unpublish and stop old track
      if (currentAudioTrack) {
        localParticipant.unpublishTrack(currentAudioTrack);
        currentAudioTrack.stop();
        console.log('🎤 [Twilio] Old audio track unpublished and stopped');
      }

      // Publish new track
      await localParticipant.publishTrack(newAudioTrack);
      console.log('✅ [Twilio] New microphone track published successfully');
    } catch (error) {
      console.error('❌ [Twilio] Error switching microphone:', error);
      throw error;
    } finally {
      this.isSwitchingMicrophone = false;
    }
  };

  // Switch camera during active call
  switchCamera = async (newDeviceId) => {
    const room = this.state.activeRoom;
    if (!room) {
      console.warn('⚠️ [Twilio] Cannot switch camera - no active room');
      return;
    }

    // Prevent concurrent camera switches
    if (this.isSwitchingCamera) {
      console.warn('⚠️ [Twilio] Camera switch already in progress, ignoring');
      return;
    }
    this.isSwitchingCamera = true;

    const { classes } = this.props;
    const localParticipant = room.localParticipant;
    console.log('📹 [Twilio] Switching camera to:', newDeviceId);

    try {
      // Get current video track
      const videoTracks = [...localParticipant.videoTracks.values()];
      const currentVideoPublication = videoTracks[0];
      const currentVideoTrack = currentVideoPublication ? currentVideoPublication.track : null;

      // Create new video track with new device
      // Use 720p for faster track creation
      const newVideoTrack = await Video.createLocalVideoTrack({
        deviceId: { exact: newDeviceId },
        width: { ideal: 1280 },
        height: { ideal: 720 },
        frameRate: { ideal: 24 }
      });

      // Detach old track from DOM
      if (currentVideoTrack) {
        currentVideoTrack.detach().forEach(element => element.remove());
        localParticipant.unpublishTrack(currentVideoTrack);
        currentVideoTrack.stop();
        console.log('📹 [Twilio] Old video track unpublished and stopped');
      }

      // Safety cleanup: Remove any orphaned local video elements (not inside #remote)
      if (this.localMedia) {
        const localVideos = this.localMedia.querySelectorAll(':scope > video');
        localVideos.forEach(video => {
          console.log('🧹 [Twilio] Removing orphaned local video element');
          video.remove();
        });
      }

      // Publish new track
      await localParticipant.publishTrack(newVideoTrack);

      // Attach new track to local video element
      if (this.localMedia) {
        const videoElement = newVideoTrack.attach();
        videoElement.className = classes.localVideo;
        this.localMedia.appendChild(videoElement);
      }

      console.log('✅ [Twilio] New camera track published successfully');
    } catch (error) {
      console.error('❌ [Twilio] Error switching camera:', error);
      throw error;
    } finally {
      this.isSwitchingCamera = false;
    }
  };

  // Progressive enhancement: Upgrade video quality from 720p to 1080p after initial fast connection
  upgradeVideoQuality = async () => {
    const room = this.state.activeRoom;
    if (!room) {
      console.warn('⚠️ [Twilio] Cannot upgrade video - no active room');
      return;
    }

    // Prevent concurrent upgrades
    if (this.isUpgradingVideo || this.hasUpgradedVideo) {
      return;
    }
    this.isUpgradingVideo = true;

    const { classes } = this.props;
    const localParticipant = room.localParticipant;
    const cameraDeviceId = localStorage.getItem("selectedConsultCamera");
    
    console.log('📹 [Twilio] Upgrading video quality to 1080p...');

    try {
      // Get current video track
      const videoTracks = [...localParticipant.videoTracks.values()];
      const currentVideoPublication = videoTracks[0];
      const currentVideoTrack = currentVideoPublication ? currentVideoPublication.track : null;

      if (!currentVideoTrack) {
        console.warn('⚠️ [Twilio] No current video track to upgrade');
        return;
      }

      // Create new 1080p video track
      // ✅ FIX: Use 'exact' constraint to force selected camera (same as switchCamera)
      const upgradeConstraints = {
        width: { ideal: 1920 },
        height: { ideal: 1080 },
        frameRate: { ideal: 24 }
      };
      if (cameraDeviceId) {
        // ✅ FIX: Use 'exact' to ensure selected camera is used, not default
        upgradeConstraints.deviceId = { exact: cameraDeviceId };
      }

      let newVideoTrack;
      try {
        newVideoTrack = await Video.createLocalVideoTrack(upgradeConstraints);
      } catch (exactErr) {
        // ✅ FIX: Fallback to 'ideal' if 'exact' fails (device might be temporarily unavailable)
        console.warn('⚠️ [Twilio] Failed to upgrade video with exact device, falling back to ideal:', exactErr.message);
        if (cameraDeviceId) {
          upgradeConstraints.deviceId = { ideal: cameraDeviceId };
        }
        newVideoTrack = await Video.createLocalVideoTrack(upgradeConstraints);
      }

      // Detach and stop old track
      currentVideoTrack.detach().forEach(element => element.remove());
      localParticipant.unpublishTrack(currentVideoTrack);
      currentVideoTrack.stop();
      // ✅ H19 FIX: Also stop the underlying MediaStreamTrack
      if (currentVideoTrack.mediaStreamTrack) {
        currentVideoTrack.mediaStreamTrack.stop();
      }

      // Publish new 1080p track
      await localParticipant.publishTrack(newVideoTrack);

      // Attach new track to local video element
      if (this.localMedia) {
        const videoElement = newVideoTrack.attach();
        videoElement.className = classes.localVideo;
        this.localMedia.appendChild(videoElement);
      }

      this.hasUpgradedVideo = true;
      console.log('✅ [Twilio] Video upgraded to 1080p successfully');
    } catch (error) {
      console.warn('⚠️ [Twilio] Video upgrade failed (non-critical):', error.message);
      // Non-critical - we still have 720p working
    } finally {
      this.isUpgradingVideo = false;
    }
  };

  // Switch speaker (audio output) during active call
  switchSpeaker = async (newDeviceId) => {
    console.log('🔊 [Twilio] Switching speaker to:', newDeviceId);

    try {
      // Find all audio elements in remote media container and update their sink
      const remoteAudioElements = this.remoteMedia ? 
        this.remoteMedia.querySelectorAll('audio') : [];
      
      for (const audioElement of remoteAudioElements) {
        if (typeof audioElement.setSinkId === 'function') {
          await audioElement.setSinkId(newDeviceId);
          console.log('🔊 [Twilio] Audio element sink updated');
        }
      }

      // Also update localStorage so future track attachments use correct device
      localStorage.setItem('selectedConsultCallingDevice', newDeviceId);
      console.log('✅ [Twilio] Speaker switched successfully');
    } catch (error) {
      console.error('❌ [Twilio] Error switching speaker:', error);
      throw error;
    }
  };

  // Toggle settings popup
  handleOpenSettings = (event) => {
    this.setState({ 
      showSettingsPopup: true, 
      settingsAnchorEl: event.currentTarget 
    });
  };

  handleCloseSettings = () => {
    this.setState({ 
      showSettingsPopup: false, 
      settingsAnchorEl: null 
    });
  };

  async joinRoom() {
    const { classes } = this.props;
    console.log('Trying to connect to Twilio with token', this.state.token);
    let previewContainer1 = this.remoteMedia;
    const component = this;
    
    // Use pre-warmed tracks if available (from parallel warmup), otherwise warm up now
    let tracks = this._preWarmedTracks;
    if (!tracks) {
      console.log('⚠️ [Twilio] No pre-warmed tracks, warming up now...');
      tracks = await this.warmupCamera();
    }
    this._preWarmedTracks = null; // Clear reference

    let hasAudio = tracks && tracks.some(t => t && (t.kind === 'audio' || (t.mediaStreamTrack && t.mediaStreamTrack.kind === 'audio')));
    let hasVideo = tracks && tracks[0] && (tracks[0].kind === 'video' || (tracks[0].mediaStreamTrack && tracks[0].mediaStreamTrack.kind === 'video'));
    if (!hasAudio) {
      console.log('⚠️ [Twilio] No audio track from warmup — retrying microphone-only acquire');
      const micDeviceId = localStorage.getItem('selectedConsultMicInput');
      const audioTrack = await acquireMicrophoneTrack(micDeviceId);
      if (audioTrack) {
        tracks = [null, audioTrack];
        hasAudio = true;
        hasVideo = false;
      }
    }
    if (!tracks || !hasAudio) {
      console.error('❌ [Twilio] Cannot proceed without microphone access');
      return;
    }
    const connectingAudioOnly = !hasVideo;
    if (connectingAudioOnly) {
      console.log('📞 [Twilio] Joining room in audio-only mode (camera unavailable or not required)');
      if (this.props.onAudioOnlyFallbackChange) {
        this.props.onAudioOnlyFallbackChange(true);
      }
    }
    
    const existingVideoTrack = tracks[0] || null;
    const existingAudioTrack = tracks[1] || (tracks[0] && tracks[0].kind === 'audio' ? tracks[0] : null);
    const tracksToPublish = [existingVideoTrack, existingAudioTrack].filter(Boolean);
    
    // Use the pre-warmed tracks instead of letting Video.connect create new ones
    Video.connect(this.state.token, {
        tracks: tracksToPublish,
        name: this.props.roomName,
        bandwidthProfile: { video: { mode: 'grid' } },
        maxAudioBitrate: 16000,
        networkQuality: { local: 1, remote: 1 }
      })
        .then((room) => {
          component.setState({
            activeRoom: room,
            localMediaAvailable: true,
            hasJoinedRoom: true,
          });
          console.log('Connected to Twilio');
          const videoTracks = [...room.localParticipant.videoTracks.values()];
          if (videoTracks.length > 0 && videoTracks[0].track) {
            const cameraTrack = videoTracks[0].track;
            const localVideoElement = cameraTrack.attach();
            localVideoElement.className = classes.localVideo;
            component.localMedia.appendChild(localVideoElement);
            console.log('✅ [Twilio] Local video attached');
            setTimeout(() => component.upgradeVideoQuality(), 2000);
          } else {
            console.log('✅ [Twilio] Connected (audio-only, no local video)');
            component.setState({ audioOnlyFallback: true });
            if (component.props.onAudioOnlyFallbackChange) {
              component.props.onAudioOnlyFallbackChange(true);
            }
          }

          // Notify parent of remote audio stream for AudioVisualizer (main consultation flow)
          function notifyRemoteAudioStream(track) {
            if (!component.props.onRemoteAudioStream) return;
            if (!track || track.kind !== 'audio') {
              component.props.onRemoteAudioStream(null);
              return;
            }
            const mt = track.mediaStreamTrack || track;
            if (mt && mt.readyState !== 'ended') {
              component.props.onRemoteAudioStream(new MediaStream([mt]));
            } else {
              component.props.onRemoteAudioStream(null);
            }
          }

          // Local function to handle participant disconnection
          function removeParticipant(participant) {
            console.log('Removing participant', participant.identity, 'from room', room.name);
            const activeRoom = component.state.activeRoom;
            const roomSidMatches = activeRoom && activeRoom.sid === room.sid;
            if (!roomSidMatches) {
              console.log('⚠️ Ignoring removeParticipant for inactive room');
              return;
            }
            if (component.props.onRemoteAudioStream) {
              component.props.onRemoteAudioStream(null);
            }
            const elem = document.getElementById('remote');
            if (elem && elem.parentNode) {
              elem.parentNode.removeChild(elem);
            }
          }

          // Local function to handle participant connection
          async function addParticipant(participant) {
            console.log('Adding participant', participant.identity, 'to room', room.name);
            const activeRoom = component.state.activeRoom;
            const roomSidMatches = activeRoom && activeRoom.sid === room.sid;
            const isInitialConnection = !activeRoom && !component.state.hasJoinedRoom;
            if (!isInitialConnection && !roomSidMatches) {
              console.log('⚠️ Ignoring addParticipant for inactive room');
              return;
            }
            for (const publication of participant.tracks.values()) {
              if (publication && publication.track && publication.isSubscribed) {
                const track = publication.track;
                const element = track.attach();
                if (track.kind === 'audio' && typeof element.setSinkId === 'function') {
                  const callOutputDeviceId = localStorage.getItem("selectedConsultCallingDevice");
                  if (callOutputDeviceId) {
                    try { await element.setSinkId(callOutputDeviceId); } catch (e) { console.warn(e); }
                  }
                }
                if (track.kind === 'video') element.className = classes.remoteVideo;
                previewContainer1.appendChild(element);
                if (track.kind === 'audio') notifyRemoteAudioStream(track);
              }
            }
            participant.on('trackSubscribed', async (track) => {
              const element = track.attach();
              if (track.kind === 'audio' && typeof element.setSinkId === 'function') {
                const callOutputDeviceId = localStorage.getItem("selectedConsultCallingDevice");
                if (callOutputDeviceId) {
                  try { await element.setSinkId(callOutputDeviceId); } catch (e) { console.warn(e); }
                }
              }
              if (track.kind === 'video') element.className = classes.remoteVideo;
              previewContainer1.appendChild(element);
              console.log('✅ Track subscribed:', track.kind);
              if (track.kind === 'audio') notifyRemoteAudioStream(track);
            });
          }

          // Set up participant handlers
        room.participants.forEach(addParticipant);
        room.on('participantConnected', addParticipant);
        room.on('participantDisconnected', removeParticipant);
          room.once('disconnected', (disconnectedRoom, error) => {
            console.log('🔌 [Twilio] Room disconnected - cleaning up', {
              roomName: disconnectedRoom.name,
              roomSid: disconnectedRoom.sid,
              error: error ? error.message : null
            });
            
            // Remove remote participants from UI
            disconnectedRoom.participants.forEach(removeParticipant);
            
            // Stop all local tracks to release camera/mic
            disconnectedRoom.localParticipant.tracks.forEach((publication) => {
              if (publication.track) {
                console.log('🛑 [Twilio] Stopping local track on disconnect:', publication.track.kind);
                publication.track.stop();
                if (publication.track.mediaStreamTrack) {
                  publication.track.mediaStreamTrack.stop();
                }
              }
            });
            
            // Update state to show disconnect message
            component.setState({
              isRoomDisconnected: true,
              connectionStatus: 'disconnected',
              activeRoom: null,
              audioOnlyFallback: false
            });

            if (component.props.onRemoteAudioStream) {
              component.props.onRemoteAudioStream(null);
            }

            // Notify parent component that room disconnected - only if NOT a local disconnect
            // Local disconnect = pharmacist called leaveRoom() (e.g., via socket event handler)
            // Remote disconnect = patient ended call from kiosk
            if (component.props.onRoomDisconnected && !component._isLocalDisconnect) {
              console.log('📢 [Twilio] Notifying parent of REMOTE room disconnect (patient ended call)');
              component.props.onRoomDisconnected(disconnectedRoom.name);
            } else if (component._isLocalDisconnect) {
              console.log('📢 [Twilio] Skipping parent notification - this was a LOCAL disconnect (pharmacist initiated)');
            }
            
            // Reset flag for next call
            component._isLocalDisconnect = false;
          });

          // Handle network instability - ICE restart attempts
          room.on('reconnecting', (error) => {
            console.log('🔄 [Twilio] Connection unstable - attempting to reconnect...', {
              roomName: room.name,
              error: error ? error.message : null
            });
            component.setState({ connectionStatus: 'reconnecting' });
          });

          room.on('reconnected', () => {
            console.log('✅ [Twilio] Connection restored', {
              roomName: room.name,
              roomSid: room.sid
            });
            component.setState({ connectionStatus: 'connected' });
          });
        })
        .catch((err) => {
          console.error('Error connecting to Twilio:', err);
          tracksToPublish.forEach(track => { if (track && typeof track.stop === 'function') track.stop(); });
      });
  }

  capturePhoto = () => {
    const { roomName } = this.props;
    let canvasPromise = html2canvas(this.remoteMedia, {
      useCORS: true
    });
    canvasPromise.then(async (canvas) => {
      await writeAuditLog(`User took screenshot of the video consultation.`, 'Consultation', { prescriptionMasterId: this.props.prescriptionMasterId });
      /* audit trail for capturing screenshot of consultation */
      // await writeAuditLog(
      //   `User took screenshot of the video consultation.`,
      //   'Consultation',
      //   { prescriptionMasterId: this.props.prescriptionMasterId },
      //   {
      //     eventType: 'CLIENT_MEDIA_CAPTURED',
      //     source: 'PORTAL',
      //     severity: 'INFO',
      //     metadata: {
      //       roomName: this.props.roomName,
      //     },
      //   }
      // );
      var dataURL = canvas.toDataURL("image/png", 0.5);
      let data = {
        code: roomName,
        type: 0,
        image_url: dataURL
      };
      await this.saveImage(data);
    });
  };

  openLightbox = (event, index) => {
    this.setState({
      currentImage: index,
      viewerIsOpen: true
    });
  };

  closeLightbox = () => {
    this.setState({
      currentImage: 0,
      viewerIsOpen: false
    });
  };

  removeImage = (e, idx) => {
    const { capturedImages } = this.state;
    const remainingItems = capturedImages.filter((item) => item !== capturedImages[idx]);
    if (remainingItems.length > 0) {
      this.setState({ capturedImages: remainingItems });
    } else {
      this.setState({ capturedImages: [] });
    }
  }

  showRejection = () => {
    this.listOfReasons();
  }

  listOfReasons = () => {
    try {
      this.setState({ showInnerSpinner: true })
      fetch(
        process.env.REACT_APP_BASEURL + "consultation/reject/reasons-list",
        {
          method: "GET",
          withCredentials: true,
          headers: {
            Authorization:
              "Bearer " +
              JSON.parse(sessionStorage.getItem("user")).signInUserSession
                .idToken.jwtToken,
            "Content-Type": "application/json",
          },
        }
      )
        .then((response) => {
          try {
            return response.json();
          } catch (error) {
            return null;
          }
        })
        .then((res) => {
          if (res && res.data) {
            this.setState({
              rejectItems: res.data,
              viewRejectReasons: true,
              showInnerSpinner: false,
            });
          }
        });
    } catch (error) {
      console.log(error)
    }
  }

  fetchExistingIds = () => {
    const { roomName } = this.props;
    fetch(
      `${process.env.REACT_APP_BASEURL}consultation/fetch-ids/${roomName}`,
      {
        method: "GET",
        withCredentials: true,
        headers: {
          Authorization:
            "Bearer " +
            JSON.parse(sessionStorage.getItem("user")).signInUserSession.idToken
              .jwtToken,
          "Content-Type": "application/json",
        },
      }
    )
      .then((response) => {
        try {
          return response.json();
        } catch (error) {
          return null;
        }
      })
      .then((existingIdsRes) => {
        if (
          existingIdsRes &&
          existingIdsRes.data &&
          existingIdsRes.data.length > 0
        ) {
          this.setState({ capturedImages: existingIdsRes.data });
        } else {
          this.setState({ capturedImages: [] })
        }
      })
      .catch((error) => {
        console.log(
          `Error while uploading captured images ${error && error.message}`
        );
      });
  };

  saveImage = async (payload) => {
    const { setIdCaptureType } = this.props;
    try {
      setIdCaptureType(payload.type);
      this.setState({ captureIsInProcess: true });
      const response = await fetch(
        `${process.env.REACT_APP_BASEURL}consultation/captured/images`,
        {
          method: "POST",
          withCredentials: true,
          headers: {
            Authorization:
              "Bearer " + JSON.parse(sessionStorage.getItem("user")).signInUserSession.idToken.jwtToken,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      const result = await response.json();
      const savedImage = result && result.data;

      if (savedImage && savedImage.id) {
       console.log(`Image saved successfully with details ${JSON.stringify(savedImage)}`);
        this.setState(prev => ({
          capturedImages: [...prev.capturedImages, savedImage]
        }));
      }

      this.setState({ captureIsInProcess: false });
    } catch (error) {
      toast.error(`Error while uploading captured images ${error.message}`);
      this.setState({ captureIsInProcess: false });
    }
  };
  
  updateCaptureStatus = (e, type) => {
    e && e.preventDefault();
    const { roomName, selectedReasonId, setIdCaptureType } = this.props;
    setIdCaptureType(type);
    let data = {
      code: roomName,
      selected_reason: selectedReasonId || "630ea51e-ca12-437e-a749-e7bc7897e96f"
    };
    fetch(
      `${process.env.REACT_APP_BASEURL}consultation/captured/status`,
      {
        method: "PUT",
        withCredentials: true,
        headers: {
          Authorization:
            "Bearer " +
            JSON.parse(sessionStorage.getItem("user")).signInUserSession.idToken
              .jwtToken,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data)
      }
    )
      .then((response) => {
        try {
          return response.json();
        } catch (error) {
          return null;
        }
      })
      .then(async (updatedStatus) => {
        /* Audit trail for id captured status */
        const filteredReason = this.state.rejectItems.filter((item) => item.id === data.selected_reason)[0];
        const auditStatus = (filteredReason && filteredReason.reason) ? filteredReason.reason : 'accepted';

        if (![null, undefined, ''].includes(auditStatus)) {
          await writeAuditLog(`Captured id uploaded successfully with status '${auditStatus}'.`, 'Consultation', { prescriptionMasterId: this.props.prescriptionMasterId });
          // await writeAuditLog(
          //   `Captured id uploaded successfully with status '${auditStatus}'.`,
          //   'Consultation',
          //   { prescriptionMasterId: this.props.prescriptionMasterId },
          //   {
          //     eventType: 'CLIENT_DOCUMENT_UPLOAD_SUCCESS',
          //     source: 'PORTAL',
          //     severity: 'INFO',
          //     metadata: {
          //       status: auditStatus,
          //     },
          //   }
          // );
        }

        console.log(`Status updated successfully ${JSON.stringify(updatedStatus)}`);
        if (updatedStatus && updatedStatus.status && updatedStatus.status === 200) {
          this.setState({ viewRejectReasons: false, showInnerSpinner: false, consultStatus: true })
        } else {
          this.setState({ viewRejectReasons: false, showInnerSpinner: false, consultStatus: false })
          if (type === 0) {
            toast.error(`Error while accepting captured ids! Please contact administrator.`);
          } else {
            toast.error(`Error while rejecting captured ids! Please contact administrator.`);
          }
        }
      })
      .catch(async (error) => {
        /* Audit trail for error during id captured */
        await writeAuditLog(`Error while uploading captured images during consultation call.`, 'Consultation', { prescriptionMasterId: this.props.prescriptionMasterId });
        // await writeAuditLog(
        //   `Error while uploading captured images during consultation call.`,
        //   'Consultation',
        //   { prescriptionMasterId: this.props.prescriptionMasterId },
        //   {
        //     eventType: 'CLIENT_DOCUMENT_UPLOAD_FAILED',
        //     source: 'PORTAL',
        //     severity: 'ERROR',
        //     metadata: {
        //       error: error && error.message ? error.message : error,
        //     },
        //   }
        // );
        console.log(
          `Error while updating status ${error && error.message}`
        );
        toast.error(`Error while uploading captured images ${error && error.message}`);
      });
  }

  removeItem = (e, itemIndex, item) => {
    e && e.preventDefault();
    if (item && item.id) {
      fetch(
        `${process.env.REACT_APP_BASEURL}consultation/${item.id}`,
        {
          method: "DELETE",
          withCredentials: true,
          headers: {
            Authorization:
              "Bearer " +
              JSON.parse(sessionStorage.getItem("user")).signInUserSession.idToken
                .jwtToken,
            "Content-Type": "application/json",
          },
        }
      )
        .then((response) => {
          try {
            return response.json();
          } catch (error) {
            return null;
          }
        })
        .then((deletedItem) => {
          this.fetchExistingIds();
          /* Audit trail for screenshot(id captured) removed success */    
          writeAuditLog(`Screenshot image removed successfully.`, 'Consultation', { prescriptionMasterId: this.props.prescriptionMasterId });
          // writeAuditLog(
          //   `Screenshot image removed successfully.`,
          //   'Consultation',
          //   { prescriptionMasterId: this.props.prescriptionMasterId },
          //   {
          //     eventType: 'CLIENT_MEDIA_CAPTURE_REMOVED',
          //     source: 'PORTAL',
          //     severity: 'INFO',
          //   }
          // );
          console.log(`item removed successfully ${JSON.stringify(deletedItem)}`);
        })
        .catch((error) => {
          /* Audit trail for error during deleting screenshot(id captured) */
          writeAuditLog(`Error while deleting an screenshot.`, 'Consultation', { prescriptionMasterId: this.props.prescriptionMasterId });
          // writeAuditLog(
          //   `Error while deleting an screenshot.`,
          //   'Consultation',
          //   { prescriptionMasterId: this.props.prescriptionMasterId },
          //   {
          //     eventType: 'CLIENT_MEDIA_CAPTURE_REMOVED',
          //     source: 'PORTAL',
          //     severity: 'ERROR',
          //     metadata: {
          //       error: error && error.message ? error.message : error,
          //     },
          //   }
          // );
          console.log(
            `Error while deleting an item status ${error && error.message}`
          );
          toast.error(`Error while deleting an item ${error && error.message}`);
        });
    } else {
      this.setState((prevState) => ({
        capturedImages: prevState.capturedImages.filter((_, i) => i !== itemIndex),
      }));
    }
  }

  render() {
    const { classes, t, submitSelectedRejection, selectedReasonId, setSelectedReasonText, selectedReasonText } = this.props;
    const {
      capturedImages,
      viewerIsOpen,
      currentImage,
      viewRejectReasons,
      showInnerSpinner,
      rejectItems,
      consultStatus,
      captureIsInProcess,
      showSettingsPopup,
      settingsAnchorEl,
      hasJoinedRoom
    } = this.state;

    return (
      <div className={classes.videoColumn}>
      <div className={classes.videoStage}>
          {/* Main video container with flattened hierarchy for proper z-index layering */}
          <div
            id="video-container"
            className={classes.videoContainer}
          >
            {/* Remote video (patient/kiosk) - base layer, full size */}
            <div
              id="remote"
              ref={(ref) => (this.remoteMedia = ref)}
              className={classes.remoteVideoContainer}
            />
            {/* Local video (pharmacist) - PIP layer, small at bottom-right when connected */}
            <div
              id="local"
              ref={(ref) => (this.localMedia = ref)}
              className={hasJoinedRoom ? classes.localVideoContainer : classes.localVideoFullSize}
            />
            {/* Reconnecting overlay - shown during network instability */}
            {this.state.connectionStatus === 'reconnecting' && !this.state.isRoomDisconnected && (
              <div className={classes.reconnectingOverlay}>
                <CircularProgress size={14} sx={{ color: '#fff' }} />
                <span>Connection unstable - reconnecting...</span>
              </div>
            )}
            {/* Audio-only fallback banner - when camera was unavailable */}
            {this.state.audioOnlyFallback && hasJoinedRoom && !this.state.isRoomDisconnected && (
              <div className={classes.audioOnlyFallbackBanner}>
                <span>Video unavailable, continuing with audio only.</span>
              </div>
            )}
          {/* Device control bar overlay */}
          {hasJoinedRoom && !this.state.isRoomDisconnected && (
            <div className={classes.deviceBarOverlay}>
              <ConsultDeviceDock
                variant="pills"
                isLiveCall={hasJoinedRoom}
                isAudioOnlyConsult={this.props.isAudioOnlyConsult || this.state.audioOnlyFallback}
                onDeviceSwitch={this.handleDeviceSwitch}
              />

              {/* <IconButton
                onClick={this.handleOpenSettings}
                size="small"
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  backgroundColor: "rgba(255,255,255,0.12)",
                  border: "1px solid rgba(255,255,255,0.22)",
                  color: "white",
                  "&:hover": { backgroundColor: "rgba(255,255,255,0.18)" },
                }}
                title="Consult Settings"
              >
                <SettingsIcon fontSize="small" />
              </IconButton> */}
            </div>
          )}
            {/* Disconnect overlay - shown when consultation ends */}
            {this.state.isRoomDisconnected && (
              <div className={classes.disconnectOverlay}>
                <span>The consultation has ended</span>
              </div>
            )}
          </div>
        <Button
          onClick={this.capturePhoto}
          className={classes.primaryButton}
          disabled={captureIsInProcess || this.state.isRoomDisconnected}
          sx={{
            width: "100%",
            backgroundColor: "#E7EFFF",
            color: "#3D7DFF",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexShrink: 0,
          }}
        >
          {captureIsInProcess ? (
            <CircularProgress size={20} sx={{ color: "#3D7DFF" }} />
          ) : (
            "Capture"
          )}
        </Button>
        </div>
        <div className={classes.capturedImagesRow}>
          <Grid
            container
            sx={{
              flexWrap: "nowrap",
              display: "flex",
            }}
          >
            {capturedImages &&
              capturedImages.map((item, index) => (
                <Grid
                  item
                  key={index}
                  style={{
                    maxWidth: "100px",
                    display: "inline-grid",
                  }}
                >
                  <img
                    src={(item && item.image_url ? item.image_url : item)}
                    alt={`Snapshot ${index}`}
                    onClick={(e) => {
                      this.openLightbox(e, index);
                    }}
                    style={{ width: "100px", height: "75px", objectFit: "cover", cursor: "pointer" }}
                  />
                  <Button
                    onClick={(e) => {
                      this.removeItem(e, index, item)
                    }}
                    size="small"
                    sx={{ minWidth: 0, padding: "2px" }}
                  >
                    <DeleteIcon fontSize="small" />
                  </Button>
                </Grid>
              ))}
          </Grid>
        </div>
        {capturedImages.length > 0 ? (
          <div className={classes.forceAcceptRejectButtons}>
            <Button
              disabled={consultStatus}
              className={classes.primaryButtonVideo}
              onClick={(e) => this.updateCaptureStatus(e, 0)}
            >
              {showInnerSpinner ? (
                <CircularProgress style={{ width: "20px !important" }} />
              ) : (
                "Accept"
              )}
            </Button>
            <Button
              disabled={consultStatus}
              onClick={(e) => {
                this.showRejection(e);
              }}
              className={classes.primaryButtonVideo}
            >
              {showInnerSpinner ? (
                <CircularProgress style={{ width: "20px !important" }} />
              ) : (
                "Reject"
              )}
            </Button>
          </div>
        ) : null}
        <Dialog open={viewerIsOpen} onClose={this.closeLightbox}>
          <DialogContent style={{ padding: "0px !important" }}>
            <img
              alt={currentImage}
              src={(capturedImages[currentImage] && capturedImages[currentImage].image_url ? capturedImages[currentImage].image_url : capturedImages[currentImage])}
              style={{ width: "550px" }}
            />
          </DialogContent>
          <DialogActions style={{ textAlign: "right" }}>
            <Button
              onClick={this.closeLightbox}
              className={classes.primaryButton}
            >
              {t("Console.MovePopup.Close")}
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={viewRejectReasons}
          onClose={this.closeRejection}
          fullWidth={true}
          maxWidth={"xs"}
        >
          <DialogContent style={{ padding: "0px !important" }}>
            <FormControl style={{ width: "99%" }}>
              <FormLabel component="legend">Select reason</FormLabel>
              <Select
                value={selectedReasonId || ""}
                onChange={(e) => {
                  submitSelectedRejection(e);
                }}
                variant="standard"
                autoWidth={true}
              >
                {rejectItems.map((type) => (
                  <MenuItem
                    key={type.id}
                    value={type.id}
                    className={classes.menuItem}
                  >
                    {type.reason}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {![undefined, "undefined", null, "null", ""].includes(
              selectedReasonId
            ) &&
              selectedReasonId === "630ea51e-ca12-437e-a749-e7bc7897e96f" && (
                <FormControl style={{ width: "99%", marginTop: "15px" }}>
                  <TextField
                    id="outlined-multiline-static"
                    label="Reason"
                    multiline
                    rows={5}
                    onChange={setSelectedReasonText}
                    value={selectedReasonText}
                  />
                </FormControl>
              )}
          </DialogContent>
          <DialogActions style={{ textAlign: "right" }}>
            <Button
              onClick={(e) => this.updateCaptureStatus(e, 1)}
              className={classes.primaryButton}
            >
              {showInnerSpinner ? (
                <CircularProgress style={{ width: "20px !important" }} />
              ) : (
                "Reject"
              )}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Device Settings Popup */}
        <ConsultSettingsPopup
          anchorEl={settingsAnchorEl}
          open={showSettingsPopup}
          onClose={this.handleCloseSettings}
          isLiveCall={hasJoinedRoom}
          onDeviceSwitch={this.handleDeviceSwitch}
          t={t}
        />
      </div>
    );
  }
}

TwilioVideo.propTypes = {
  classes: PropTypes.object.isRequired,
  isAudioOnlyConsult: PropTypes.bool,
  /** Called with remote (kiosk) audio MediaStream for AudioVisualizer, or null when none */
  onRemoteAudioStream: PropTypes.func,
  /** Fired when consult connects without local video (camera missing/unavailable) */
  onAudioOnlyFallbackChange: PropTypes.func,
};

TwilioVideo.defaultProps = {
  isAudioOnlyConsult: false,
  onRemoteAudioStream: undefined,
  onAudioOnlyFallbackChange: undefined,
};

export default withStyles(styles)(TwilioVideo);



// WEBPACK FOOTER //
// ./src/components/TwilioProgrammableVideo/TwilioVideo.js